
import { SortModel } from '@components/DataTable/types';
import { TalentOperationsProvider } from '@modules/talents/context/TalentOperationsContext';
import { TalentSearchFilter } from '@modules/talent-search/TalentSearchFilter';
import { useTalentSearch } from '@modules/talent-search/hooks/useTalentSearch';

import Stack from '@mui/material/Stack';
import { useCallback, useMemo } from 'react';
import TalentFavoritesList from './components/TalentFavoritesDataTable';

const DEFAULT_SORT_FIELD = 'CREATED_AT';
const DEFAULT_SORT_ORDER = 'DESC';

/**
 * Main container component for Talent Favorites management.
 * Uses unified talent search with FAVORITES context to support presets and cascading filters.
 */
const TalentFavoritesModule = () => {
    const {
        filters,
        setFilters,
        updateFilter,
        setSortBy,
        sortBy,
        talents,
        totalDocs,
        loading,
        isLoadingMore,
        hasMore,
        loadMore,
        error,
        refetch,
        availableFilters,
        page,
        pageSize,
        handlePageChange,
        handlePageSizeChange,
    } = useTalentSearch({ context: 'FAVORITES' });

    // Derive sortModel for DataTable from current sort state
    const sortModel = useMemo<SortModel[]>(() => {
        const fieldToColumnIdMap: Record<string, string> = {
            NAME: 'name',
            RATING: 'rating',
            CITY: 'city',
            EXPERIENCE: 'experience',
            TOTAL_APPLICATIONS: 'applications',
            CREATED_AT: 'createdAt',
        };
        const columnId = fieldToColumnIdMap[sortBy.field];
        if (!columnId) return [];
        return [{ field: columnId, sort: sortBy.order === 'ASC' ? 'asc' : 'desc' }];
    }, [sortBy]);

    const handleTableSortChange = useCallback((model: SortModel[]) => {
        const columnToFieldMap: Record<string, string> = {
            name: 'NAME',
            rating: 'RATING',
            city: 'CITY',
            experience: 'EXPERIENCE',
            applications: 'TOTAL_APPLICATIONS',
            createdAt: 'CREATED_AT',
        };

        const activeSort = model[0];
        if (!activeSort?.sort) {
            setSortBy({ field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_ORDER });
            return;
        }

        const field = columnToFieldMap[activeSort.field];
        if (!field) return;

        setSortBy({ field, order: activeSort.sort === 'asc' ? 'ASC' : 'DESC' });
    }, [setSortBy]);

    const handleQueryChange = useCallback((value: string) => {
        updateFilter('query', value);
    }, [updateFilter]);

    return (
        <TalentOperationsProvider>
            <Stack spacing={2} paddingTop={0} height="100%" flex={1}>
                <TalentSearchFilter
                    context="FAVORITES"
                    totalDocs={totalDocs}
                    onQueryChange={handleQueryChange}
                    onFilterChange={setFilters}
                    filter={filters}
                    availableFilters={availableFilters}
                />
                <TalentFavoritesList
                    talents={talents}
                    onRefetch={refetch}
                    loading={loading}
                    isLoadingMore={isLoadingMore}
                    onScrollEnd={hasMore ? loadMore : undefined}
                    error={error}
                    pagination={{
                        currentPage: page + 1,
                        pageSize,
                        totalRows: totalDocs,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                    sortModel={sortModel}
                    onSortChange={handleTableSortChange}
                />
            </Stack>
        </TalentOperationsProvider>
    );
};

export default TalentFavoritesModule;
